import { memo, useEffect, useState } from 'react'

import { Close, Plus } from '@icon-park/react'

import { Alert, Api } from '@/modules'
import { useObserver } from '@/modules/observer'
import { useRouting } from '@/hooks'

import { Button, Flex, Input } from '@/components'

import { PUBLISHER_PERSONS_TABLE } from '../table/PublisherPersonsTable.component'
import { GroupTagInput } from '../groupInput/PublisherPersonsGroupInput.component'
import {
  SectionTitle,
  FieldGroup,
  FieldLabel,
  FieldHint,
  RequiredMark,
  ProfileRow,
  AddNetworkBtn,
} from './PublisherPersonsForm.style'
import { PlatformSelector } from '../plataformSelector/PublisherPersonsPlatformSelector.component'

type Profile = {
  platform: string
  profileUrl: string
}

type Group = {
  id: string
  name: string
  isGlobal: boolean
}

type Props = {
  onSuccess?: () => void
}

export const PublisherPersonsForm = memo(Component)

function Component({ onSuccess }: Props): JSX.Element | null {
  const { companyID } = useRouting()
  const { sendMessage } = useObserver()
  const { sendAlert } = Alert.Hook()

  const [personName, setPersonName] = useState('')
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([])
  const [profiles, setProfiles] = useState<Profile[]>([{ platform: 'Twitter', profileUrl: '' }])
  const [groups, setGroups] = useState<Group[]>([])

  const { run: fetchGroups } = Api.Command<Group[]>(Api.Methods.GET, `companies/:companyID/publisher-persons/groups`)

  const { run: createGroup } = Api.Command<{ id: string; name: string }>(
    Api.Methods.POST,
    `companies/:companyID/publisher-persons/groups`,
  )

  const { isRunning: isCreating, run: createPerson } = Api.Command<void>(
    Api.Methods.POST,
    `companies/:companyID/publisher-persons`,
  )

  useEffect(() => {
    if (!companyID) return
    fetchGroups({
      binds: [['companyID', companyID]],
      onSuccess: (data) => setGroups(data ?? []),
    })
  }, [companyID])

  function addProfile(): void {
    setProfiles((prev) => [...prev, { platform: 'Twitter', profileUrl: '' }])
  }

  function removeProfile(index: number): void {
    setProfiles((prev) => prev.filter((_, i) => i !== index))
  }

  function updateProfile(index: number, field: keyof Profile, value: string): void {
    setProfiles((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)))
  }

  function handleCreateGroup(name: string, onCreated: (group: Group) => void): void {
    createGroup({
      input: { name },
      binds: [['companyID', companyID!]],
      onSuccess: (data) => {
        if (!data) return
        const newGroup: Group = { id: data.id, name: data.name, isGlobal: false }
        fetchGroups({
          binds: [['companyID', companyID!]],
          onSuccess: (updatedGroups) => {
            setGroups(updatedGroups ?? [])
            onCreated(newGroup)
          },
        })
      },
    })
  }

  function resetForm(): void {
    setPersonName('')
    setSelectedGroups([])
    setProfiles([{ platform: 'Twitter', profileUrl: '' }])
  }

  function onSubmit(): void {
    if (!personName.trim()) {
      return sendAlert({ type: 'error', title: 'Campo obrigatório', message: 'Informe o nome da pessoa' })
    }
    if (!selectedGroups.length) {
      return sendAlert({ type: 'error', title: 'Campo obrigatório', message: 'Selecione pelo menos um grupo' })
    }
    if (profiles.some((p) => !p.profileUrl.trim())) {
      return sendAlert({ type: 'error', title: 'Campo obrigatório', message: 'Preencha todos os links de perfil' })
    }

    createPerson({
      input: {
        name: personName.trim(),
        groupIDs: selectedGroups.map((g) => g.id),
        profiles,
      },
      binds: [['companyID', companyID!]],
      onSuccess: () => {
        sendAlert({ type: 'success', title: 'Sucesso', message: 'Pessoa cadastrada com sucesso!' })
        resetForm()
        sendMessage(PUBLISHER_PERSONS_TABLE)
        onSuccess?.()
      },
    })
  }

  if (!companyID) return null

  return (
    <Flex $type="column" $align="stretch" $gap={16}>
      <SectionTitle>Cadastro manual</SectionTitle>

      {/* nome + grupos */}
      <Flex $type="row" $align="flex-start" $gap={16} $fit>
        <FieldGroup>
          <FieldLabel>
            Nome <RequiredMark>*</RequiredMark>
          </FieldLabel>
          <Input placeholder="Ex: Carlos Bolsonaro" value={personName} onChange={setPersonName} />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>
            Grupos <RequiredMark>*</RequiredMark>
          </FieldLabel>
          <GroupTagInput
            groups={groups}
            selected={selectedGroups}
            onChange={setSelectedGroups}
            onCreate={handleCreateGroup}
          />
        </FieldGroup>
      </Flex>

      {/* perfis */}
      <Flex $type="column" $align="stretch" $gap={8}>
        <FieldLabel>
          Perfis por rede <RequiredMark>*</RequiredMark>
        </FieldLabel>
        <FieldHint>Para mais de um perfil na mesma rede, separe os links por ponto e vírgula (;)</FieldHint>
        {profiles.map((profile, index) => (
          <ProfileRow key={index}>
            <PlatformSelector value={profile.platform} onChange={(val) => updateProfile(index, 'platform', val)} />
            <Input
              placeholder="https://twitter.com/perfil;https://twitter.com/perfil2"
              value={profile.profileUrl}
              onChange={(val: string) => updateProfile(index, 'profileUrl', val)}
            />
            {profiles.length > 1 && (
              <Button.Root $variant="link" onClick={() => removeProfile(index)}>
                <Button.Icon value={Close} />
              </Button.Root>
            )}
          </ProfileRow>
        ))}
        <AddNetworkBtn $variant="outlined" onClick={addProfile}>
          <Button.Icon value={Plus} />
          <Button.Text value="Adicionar rede" />
        </AddNetworkBtn>
      </Flex>

      {/* ações */}
      <Flex $type="row" $justify="flex-end" $gap={12}>
        <Button.Root $variant="outlined" onClick={resetForm} disabled={isCreating}>
          <Button.Text value="Limpar" />
        </Button.Root>
        <Button.Root $variant="primary" onClick={onSubmit} $loading={isCreating}>
          <Button.Text value="Adicionar pessoa" />
        </Button.Root>
      </Flex>
    </Flex>
  )
}
